import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Calendar, Users } from 'lucide-react'
import { renderMarkdown } from '../utils/markdown'
import { AnimatePresence, motion } from 'motion/react'
import {
    API_HOST,
    AdviceChatAdvisor,
    AdviceChatMessage,
    AdviceChatPayload,
    getAdviceChat,
    goToLoginIfSessionClosed,
} from '../api/client'
import { UserContext } from '../components/UserContext'
import ChatInterface, { ChatMessage } from '../components/ChatInterface'
import Spinner from '../components/Spinner'

// Stage 5: unified advice chat UI. Renders an advice-kind genie chat from
// the `genie_chats` substrate. Mirrors GenieChat's websocket plumbing
// (`/api/genie/ws/{chat_id}`) — Stage 2's moderator loop streams advisor
// turns as `type:'chunk'` events with an `advisor_id` field, which we map
// back to `config.advisors[id]` for avatar + name.

interface AdviceMessage extends ChatMessage {
    authorId?: number | null
    chatMessageId?: number
}

// `sys:*` and moderator-only assistant rows are LLM-context only; the
// plan's render rule is to hide them from the bubble stream.
const isHiddenMessageType = (msg: AdviceChatMessage): boolean => {
    if (msg.role === 'system') return true
    if (msg.role === 'function') return true
    const t = msg.message_type || ''
    if (t.startsWith('sys:')) return true
    if (t === 'ass:__control') return true
    if (t === 'ass:__text') return true
    return false
}

// Translate a backend message row (REST or websocket reload) into the
// shape ChatInterface renders.
const toAdviceMessage = (m: AdviceChatMessage): AdviceMessage | null => {
    if (isHiddenMessageType(m)) return null
    if (m.role === 'user') {
        return {
            chatMessageId: m.id,
            role: 'user',
            content: m.content,
        }
    }
    if (m.role === 'assistant') {
        // Only `ass:advisor_message` survives the filter above (because
        // `ass:__control` / `ass:__text` are hidden). Stamp author_id on
        // the bubble so the renderer can look up the persona avatar.
        return {
            chatMessageId: m.id,
            role: 'assistant',
            content: m.content,
            renderedContent: renderMarkdown(m.content),
            authorId: m.author_id ?? null,
            messageType: m.message_type ?? undefined,
        }
    }
    return null
}

const advisorDisplayName = (advisor: AdviceChatAdvisor | undefined): string => {
    if (!advisor) return 'Advisor'
    const nickname = advisor.nickname || ''
    const first = advisor.first_name || ''
    const last = advisor.last_name || ''
    const fullName = `${first} ${last}`.trim()
    if (nickname) return fullName ? `${nickname} (${fullName})` : nickname
    if (fullName) return fullName
    if (advisor.archetype) return advisor.archetype
    return 'Advisor'
}

const advisorAvatarUrl = (advisor: AdviceChatAdvisor | undefined): string => {
    return advisor?.profile_picture_url || '/static/images/default_avatar.jpg'
}

const AdviceChatPage = () => {
    const { chatId: chatIdFromUrl } = useParams<{ chatId: string }>()
    const navigate = useNavigate()
    const { user } = useContext(UserContext)

    const [payload, setPayload] = useState<AdviceChatPayload | null>(null)
    const [messages, setMessages] = useState<AdviceMessage[]>([])
    const [activeAdvisorId, setActiveAdvisorId] = useState<number | null>(null)
    const [isConnecting, setIsConnecting] = useState(true)
    const [isTyping, setIsTyping] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const socket = useRef<WebSocket | null>(null)
    const isConnectingRef = useRef(false)
    // Chunks are keyed by backend message_id; we dedupe on chunk_index so a
    // re-emitted chunk after reconnect doesn't double-write.
    const chunkIndexById = useRef<Map<number, Set<number>>>(new Map())

    // -- 1. Fetch the chat payload up front (REST). We can't rely on the
    // websocket reload because `genie_router.py`'s reload path doesn't
    // include `author_id` on `ass:advisor_message` rows — and without it,
    // we can't render speaker attribution. The REST endpoint
    // `/api/genie/chats/{id}/advice` is dedicated for this surface and
    // returns the advisor squad + every message row (including author_id).
    useEffect(() => {
        if (!chatIdFromUrl) {
            navigate('/advice', { replace: true })
            return
        }
        let cancelled = false
        const load = async () => {
            try {
                const data = await getAdviceChat(chatIdFromUrl)
                if (cancelled) return
                setPayload(data)
                const bubbles = data.messages
                    .map(toAdviceMessage)
                    .filter((m): m is AdviceMessage => m !== null)
                setMessages(bubbles)
            } catch (e) {
                if (cancelled) return
                console.error('Failed to load advice chat:', e)
                setError('Failed to load chat')
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [chatIdFromUrl, navigate])

    // -- 2. Connect the websocket. The genie router replays history on
    // open as `type:'message'` rows without author_id — we ignore those
    // because step 1's REST fetch already covers initial state. We only
    // consume live events: `chunk` / `complete` (advisor streaming),
    // `typing` (with advisor_id), and the end-of-turn signals.
    useEffect(() => {
        if (!user || !chatIdFromUrl) return
        connectWebSocket(chatIdFromUrl)
        return () => {
            if (socket.current && socket.current.readyState === WebSocket.OPEN) {
                socket.current.close()
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, chatIdFromUrl])

    const connectWebSocket = (chatId: string) => {
        if (isConnectingRef.current) return
        if (socket.current && socket.current.readyState === WebSocket.OPEN) return

        isConnectingRef.current = true
        const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
        const wsUrl = `${protocol}://${API_HOST}/api/genie/ws/${chatId}`
        const ws = new WebSocket(wsUrl)
        socket.current = ws

        ws.onopen = () => {
            isConnectingRef.current = false
            setIsConnecting(false)
        }

        ws.onerror = (err) => {
            console.error('Advice ws error:', err)
            isConnectingRef.current = false
            setError('Connection error.')
            setIsConnecting(false)
            setIsTyping(false)
        }

        ws.onclose = (event) => {
            isConnectingRef.current = false
            goToLoginIfSessionClosed(event)
        }

        ws.onmessage = (event) => {
            const data = JSON.parse(event.data)

            if (data.type === 'typing') {
                if (typeof data.advisor_id === 'number') {
                    setActiveAdvisorId(data.advisor_id)
                }
                setIsTyping(true)
                return
            }

            if (data.type === 'chunk' && data.message_type === 'ass:advisor_message') {
                const messageId = data.message_id as number
                const chunkIndex = data.chunk_index as number
                const advisorId = data.advisor_id as number | undefined

                if (!chunkIndexById.current.has(messageId)) {
                    chunkIndexById.current.set(messageId, new Set())
                }
                const seen = chunkIndexById.current.get(messageId)!
                if (seen.has(chunkIndex)) return
                seen.add(chunkIndex)

                if (typeof advisorId === 'number') setActiveAdvisorId(advisorId)
                setIsTyping(true)

                setMessages((prev) => {
                    const last = prev[prev.length - 1]
                    if (last && last.chatMessageId === messageId) {
                        const newContent = last.content + data.content
                        const updated: AdviceMessage = {
                            ...last,
                            content: newContent,
                            renderedContent: renderMarkdown(newContent),
                            pending: true,
                        }
                        return [...prev.slice(0, -1), updated]
                    }
                    return [
                        ...prev,
                        {
                            chatMessageId: messageId,
                            role: 'assistant',
                            content: data.content,
                            renderedContent: renderMarkdown(data.content),
                            pending: true,
                            authorId: advisorId ?? null,
                            messageType: 'ass:advisor_message',
                        },
                    ]
                })
                return
            }

            if (data.type === 'complete' && data.message_type === 'ass:advisor_message') {
                const messageId = data.message_id as number
                const advisorId = data.advisor_id as number | undefined
                setMessages((prev) =>
                    prev.map((m) =>
                        m.chatMessageId === messageId
                            ? {
                                  ...m,
                                  content: data.content,
                                  renderedContent: renderMarkdown(data.content),
                                  pending: false,
                                  authorId: advisorId ?? m.authorId ?? null,
                              }
                            : m,
                    ),
                )
                setIsTyping(false)
                return
            }

            if (data.type === 'complete_conversation') {
                setIsTyping(false)
                return
            }

            if (data.type === 'error') {
                setError(data.content || 'Something went wrong.')
                setIsTyping(false)
                return
            }

            if (data.type === 'quota_error') {
                setError(data.message || 'You hit the LLM quota.')
                setIsTyping(false)
                return
            }

            // type=='message' on reload: ignore. The REST fetch is the
            // source of truth for initial state, and that payload includes
            // the author_id field this reload payload lacks.
        }
    }

    const sendMessage = (text: string) => {
        const trimmed = text.trim()
        if (!trimmed) return
        if (!socket.current || socket.current.readyState !== WebSocket.OPEN) {
            setError('Connection not ready — try again in a moment.')
            return
        }
        // Add an optimistic user bubble. The backend writes the row
        // server-side and never echoes it back.
        setMessages((prev) => [
            ...prev,
            { role: 'user', content: trimmed },
        ])
        setIsTyping(true)
        socket.current.send(
            JSON.stringify({
                type: 'message',
                content: trimmed,
                user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            }),
        )
    }

    const advisorsById = useMemo(() => {
        const out: Record<number, AdviceChatAdvisor> = {}
        for (const a of payload?.chat.advisors || []) {
            if (typeof a.id === 'number') out[a.id] = a
        }
        return out
    }, [payload])

    // Custom renderer: advisor bubbles get an avatar + name header, looked
    // up against the chat's `config.advisors[author_id]`.
    const renderAdviceMessage = (msg: AdviceMessage, index: number) => {
        const isUser = msg.role === 'user'
        if (isUser) {
            return (
                <div
                    key={msg.chatMessageId ?? `u-${index}`}
                    className="mb-4 text-right"
                    data-author-id="user"
                >
                    <div className="inline-block p-3 rounded-lg text-base bg-action text-plum rounded-br-none">
                        {msg.content}
                    </div>
                </div>
            )
        }

        const advisor =
            msg.authorId !== null && msg.authorId !== undefined
                ? advisorsById[msg.authorId]
                : undefined
        const name = advisorDisplayName(advisor)
        const avatar = advisorAvatarUrl(advisor)

        return (
            <div
                key={msg.chatMessageId ?? `a-${index}`}
                className="mb-4"
                data-author-id={msg.authorId ?? 'unknown'}
            >
                <div
                    className={`inline-block p-3 rounded-lg text-base bg-gray-100 text-gray-800 w-full ${
                        msg.pending ? 'opacity-70' : ''
                    }`}
                >
                    <div className="flex items-center mb-2">
                        <img
                            src={avatar}
                            alt={`${name} avatar`}
                            className="w-8 h-8 rounded-full object-cover mr-2"
                            data-testid={`advisor-avatar-${msg.authorId ?? 'unknown'}`}
                        />
                        <div className="font-semibold text-sm text-amber-900">
                            {name}
                        </div>
                    </div>
                    <div
                        className="prose prose-base max-w-none"
                        dangerouslySetInnerHTML={{
                            __html: msg.renderedContent || msg.content,
                        }}
                    />
                    {msg.pending && (
                        <span className="inline-block animate-pulse ml-1">▋</span>
                    )}
                </div>
            </div>
        )
    }

    const activeAdvisor =
        activeAdvisorId !== null ? advisorsById[activeAdvisorId] : undefined

    const typingIndicator = isTyping ? (
        <div className="mb-4 text-sm text-gray-600 italic">
            {activeAdvisor
                ? `${advisorDisplayName(activeAdvisor)} is typing...`
                : 'Thinking...'}
        </div>
    ) : null

    if (isConnecting && !payload) {
        return (
            <div className="flex items-center justify-center h-screen">
                <Spinner />
            </div>
        )
    }

    const project = payload?.chat.project
    const allAdvisors = payload?.chat.advisors || []

    return (
        // Full height below the nav (h-16) and the shell's `py-4`, so the chat
        // column can stretch and pin the composer to the bottom of the screen.
        <div
            className="container mx-auto px-4 relative flex flex-col min-h-[calc(100dvh-6rem)] shell-fill-height"
            data-testid="advice-chat-page"
        >
            <div className="flex justify-center mt-4 mb-6">
                <div className="w-full lg:w-2/3 xl:w-1/2">
                    <div className="flex items-center justify-between">
                        <div>
                            <h2 className="text-2xl font-bold mb-2">
                                {project?.emoji && (
                                    <span className="mr-1">{project.emoji}</span>
                                )}
                                {project?.title || 'Squad Advice'}
                            </h2>
                            <h3 className="text-lg text-gray-600 mb-4">
                                {payload?.chat.title || 'New Chat'}
                            </h3>
                        </div>
                        {/* Active speaker — uses `layoutId` so a swap between
                            advisors animates as a slide rather than a hard
                            replace. Mirrors the surface-pop preset from CLAUDE.md
                            ({type:'spring', stiffness:500, damping:30}). */}
                        <div
                            className="w-12 h-12 flex items-center justify-center"
                            data-testid="active-speaker-slot"
                        >
                            <AnimatePresence mode="popLayout">
                                {activeAdvisor && (
                                    <motion.div
                                        key={`active-${activeAdvisor.id}`}
                                        layoutId="advice-active-speaker"
                                        initial={{ opacity: 0, scale: 0.6 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        exit={{ opacity: 0, scale: 0.6 }}
                                        transition={{
                                            type: 'spring',
                                            stiffness: 500,
                                            damping: 30,
                                        }}
                                        data-testid={`active-advisor-${activeAdvisor.id}`}
                                    >
                                        <img
                                            src={advisorAvatarUrl(activeAdvisor)}
                                            alt={`${advisorDisplayName(activeAdvisor)} avatar`}
                                            className="w-12 h-12 rounded-full object-cover ring-2 ring-action"
                                        />
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    </div>

                    <div className="flex flex-col mb-4 text-sm text-gray-600">
                        {payload?.chat.created_at && (
                            <div className="flex items-center mb-2">
                                <Calendar size={16} className="mr-1" />
                                <span>
                                    Started:{' '}
                                    {new Date(
                                        payload.chat.created_at,
                                    ).toLocaleDateString(undefined, {
                                        month: 'short',
                                        day: 'numeric',
                                        year: 'numeric',
                                    })}
                                </span>
                            </div>
                        )}
                        {allAdvisors.length > 0 && (
                            // Wraps rather than scrolls: a squad can be seven
                            // advisors, and every name should be readable
                            // without swiping. One chip per advisor.
                            <div
                                className="flex flex-wrap items-center gap-1"
                                data-testid="advice-squad"
                            >
                                <span className="flex items-center mr-1">
                                    <Users size={16} className="mr-1 flex-shrink-0" />
                                    Squad:
                                </span>
                                {allAdvisors.map((a) => (
                                    <span
                                        key={a.id}
                                        className="inline-flex items-center max-w-full rounded-full bg-white/70 py-0.5 pl-0.5 pr-2"
                                    >
                                        <img
                                            src={advisorAvatarUrl(a)}
                                            alt={`${advisorDisplayName(a)} avatar`}
                                            className="w-6 h-6 rounded-full object-cover mr-1 flex-shrink-0"
                                        />
                                        <span className="min-w-0 break-words">
                                            {advisorDisplayName(a)}
                                        </span>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {project?.description && (
                        <p className="text-gray-600 mb-4">{project.description}</p>
                    )}
                </div>
            </div>

            <div className="flex flex-1 justify-center mt-4">
                <div className="w-full lg:w-2/3 xl:w-1/2 flex flex-col">
                    <ChatInterface
                        className="flex-1"
                        messages={messages as ChatMessage[]}
                        onSendMessage={sendMessage}
                        isLoading={isTyping || isConnecting}
                        disabled={isConnecting}
                        renderMessage={renderAdviceMessage as any}
                        typingIndicator={typingIndicator}
                        footerContent={
                            error && (
                                <div className="text-rose-500 text-sm mb-2">
                                    {error}
                                </div>
                            )
                        }
                    />
                </div>
            </div>
        </div>
    )
}

export default AdviceChatPage
